import type { WineListRenderItem, WineListRenderSection } from '../types/venue';

/**
 * Wine-list search normalisation.
 *
 * Wine names, producers, regions, and grapes routinely include accented
 * characters, apostrophes, hyphens, and other punctuation that generic text
 * search handles poorly. This module exposes one matcher used by the public
 * customer view (and any future surfaces that want the same semantics).
 *
 * The pipeline is:
 *   1. NFKD normalise + strip combining diacritics ("Kékfrankos" → "Kekfrankos")
 *   2. Lowercase
 *   3. Replace every non-alphanumeric run with a single space
 *      ("d'Yquem" → "d yquem", "Gevrey-Chambertin" → "gevrey chambertin")
 *   4. Trim and collapse whitespace
 *
 * Both the query and the haystack are passed through the same pipeline. A
 * query "matches" if every whitespace-separated token in the normalised query
 * appears as a substring of the normalised haystack — so partial matches
 * across different fields work ("gevrey" matches a wine in the
 * Gevrey-Chambertin region; "meursault" matches a wine whose name says it).
 *
 * No autocomplete, no fuzzy edit-distance, no "did you mean" — those are
 * intentionally future work. See the wine-list module doc for notes on
 * wine-aware suggestion using producer/region/wine-name vocabulary.
 */

const COMBINING_DIACRITICS = /[̀-ͯ]/g;
const NON_ALPHANUM_RUN = /[^a-z0-9]+/g;

/** Normalise an arbitrary string for search comparison. Empty / null input → "". */
export const normalizeForSearch = (input: string | null | undefined): string => {
  if (!input) return '';
  return input
    .normalize('NFKD')
    .replace(COMBINING_DIACRITICS, '')
    .toLowerCase()
    .replace(NON_ALPHANUM_RUN, ' ')
    .trim();
};

/**
 * True when every token in the (already-normalised) query is a substring of
 * the (already-normalised) haystack. Empty query → always true (caller should
 * bypass filtering altogether for an empty search).
 */
export const matchesQuery = (haystack: string, query: string): boolean => {
  const q = normalizeForSearch(query);
  if (!q) return true;
  const tokens = q.split(' ');
  for (const token of tokens) {
    if (!token) continue;
    if (!haystack.includes(token)) return false;
  }
  return true;
};

/**
 * Build a single normalised search blob for one item, mixing in its section's
 * name + description so that a query like "burgundy" matches every wine in
 * the Burgundy section even when the word doesn't appear on the item itself.
 *
 * Customer-safe fields ONLY. Must never include:
 *   binNumber, stockQuantity, stockLabel, availabilityBand,
 *   inventoryItemId, source, needsReview, internal IDs.
 */
export const buildItemSearchText = (
  item: WineListRenderItem,
  section: Pick<WineListRenderSection, 'name' | 'description'>,
): string => {
  const parts: Array<string | number | null | undefined> = [
    item.name,
    item.producer,
    item.vintage,
    item.region,
    // W6.4: country + region hierarchy. Including these means "france"
    // matches every French wine and "burgundy" matches a Meursault even if
    // the snapshot region only stores "Meursault".
    item.country,
    item.regionDisplay,
    item.color,
    section.name,
    section.description,
    item.notes,
    item.currency,
  ];

  // Add each region-path level as its own searchable token so partial
  // matches like "côte" find Côte de Beaune wines.
  if (Array.isArray(item.regionPath)) {
    for (const r of item.regionPath) parts.push(r);
  }

  if (item.bottleSize != null) {
    // Both "ml" and "l" forms so "750ml" or "0.75l" both hit.
    parts.push(`${item.bottleSize}ml`);
    parts.push(`${item.bottleSize / 1000}l`);
  }
  if (item.bottlePrice != null) {
    parts.push(String(item.bottlePrice));
  }

  for (const fmt of item.formats ?? []) {
    parts.push(fmt.name);
    parts.push(`${fmt.volumeMl}ml`);
    parts.push(String(fmt.price));
  }

  // Availability is part of the customer-safe DTO too — searching "out of stock"
  // surfaces unavailable wines.
  if (item.bottlesAvailable != null) {
    if (item.bottlesAvailable === 0) parts.push('out of stock');
    else if (item.bottlesAvailable === 1) parts.push('1 bottle');
    else parts.push(`${item.bottlesAvailable} bottles`);
  }

  return parts
    .filter((p): p is string | number => p !== null && p !== undefined && p !== '')
    .map(p => normalizeForSearch(String(p)))
    .filter(Boolean)
    .join(' ');
};

// ─────────────────────────────────────────────────────────────────────────────
// W6.5 — RELEVANCE RANKING
// ─────────────────────────────────────────────────────────────────────────────
//
// Given an active query, score each item by where its tokens matched. A
// token in the wine NAME outranks a token in the country, which outranks a
// token in the notes. The score is just `sum(best-priority-field-weight)`
// across the query tokens — caller sorts items by score DESC.
//
// Score is only meaningful while a query is active; with no query the
// component falls back entirely to the user's selected sort mode.

/** Field priority tiers used by scoreWineListItem. Higher = more relevant. */
const FIELD_WEIGHT = {
  /** Primary visual identifier — wine name, vintage. */
  PRIMARY: 100,
  /** Producer / domain. */
  PRODUCER: 60,
  /** Region the wine renders with: snapshot region, regionDisplay, final regionPath element. */
  DIRECT_REGION: 30,
  /** Country, parent regions in the path, section name/description. */
  BROADER: 10,
  /** Notes, color, bottle size, prices, format names, availability text. */
  SECONDARY: 1,
} as const;

interface ScoredField {
  weight: number;
  /** Pre-normalised haystack so the inner loop is just a substring test. */
  text: string;
}

/**
 * Build the per-field scoring fragments for one item. Each entry is checked
 * for token-substring presence; the highest matching weight per token wins.
 */
const collectScoredFields = (
  item: WineListRenderItem,
  section: Pick<WineListRenderSection, 'name' | 'description'>,
): ScoredField[] => {
  const fields: ScoredField[] = [];
  const push = (weight: number, value: string | number | null | undefined) => {
    if (value == null || value === '') return;
    const text = normalizeForSearch(String(value));
    if (text) fields.push({ weight, text });
  };

  // A — Primary display: wine name (and any composite text the server packs
  //     into it — producer, grape, appellation, vintage).
  push(FIELD_WEIGHT.PRIMARY, item.name);
  push(FIELD_WEIGHT.PRIMARY, item.vintage);

  // B — Producer.
  push(FIELD_WEIGHT.PRODUCER, item.producer);

  // C — Direct region: snapshot region, the joined regionDisplay string, and
  //     the final element of regionPath (the most specific appellation).
  push(FIELD_WEIGHT.DIRECT_REGION, item.region);
  push(FIELD_WEIGHT.DIRECT_REGION, item.regionDisplay);
  if (Array.isArray(item.regionPath) && item.regionPath.length > 0) {
    push(FIELD_WEIGHT.DIRECT_REGION, item.regionPath[item.regionPath.length - 1]);
  }

  // D — Broader hierarchy: country, parent regions in the path, section.
  push(FIELD_WEIGHT.BROADER, item.country);
  if (Array.isArray(item.regionPath) && item.regionPath.length > 1) {
    for (let i = 0; i < item.regionPath.length - 1; i++) {
      push(FIELD_WEIGHT.BROADER, item.regionPath[i]);
    }
  }
  push(FIELD_WEIGHT.BROADER, section.name);
  push(FIELD_WEIGHT.BROADER, section.description);

  // E — Secondary supporting fields. Low weight so a stray "red" in notes
  //     doesn't outrank a wine literally named with the query.
  push(FIELD_WEIGHT.SECONDARY, item.notes);
  push(FIELD_WEIGHT.SECONDARY, item.color);
  push(FIELD_WEIGHT.SECONDARY, item.currency);
  if (item.bottleSize != null) {
    push(FIELD_WEIGHT.SECONDARY, `${item.bottleSize}ml`);
    push(FIELD_WEIGHT.SECONDARY, `${item.bottleSize / 1000}l`);
  }
  if (item.bottlePrice != null) push(FIELD_WEIGHT.SECONDARY, String(item.bottlePrice));
  for (const fmt of item.formats ?? []) {
    push(FIELD_WEIGHT.SECONDARY, fmt.name);
    push(FIELD_WEIGHT.SECONDARY, `${fmt.volumeMl}ml`);
    push(FIELD_WEIGHT.SECONDARY, String(fmt.price));
  }
  if (item.bottlesAvailable != null) {
    if (item.bottlesAvailable === 0) push(FIELD_WEIGHT.SECONDARY, 'out of stock');
    else if (item.bottlesAvailable === 1) push(FIELD_WEIGHT.SECONDARY, '1 bottle');
    else push(FIELD_WEIGHT.SECONDARY, `${item.bottlesAvailable} bottles`);
  }

  return fields;
};

/**
 * Relevance score for one (item, section, query) triple.
 *
 * For each whitespace-separated query token, find the highest-priority field
 * whose normalised text contains the (normalised) token, and add that
 * field's weight to the running total. Tokens that don't match any field
 * contribute 0. With no query the score is 0 for everything, which is the
 * caller's signal to fall back to the user-selected sort mode.
 *
 * Token-AND filter is unchanged — it still happens in matchesQuery; this
 * function only ranks the wines that already passed the filter.
 */
export const scoreWineListItem = (
  item: WineListRenderItem,
  section: Pick<WineListRenderSection, 'name' | 'description'>,
  query: string,
): number => {
  const q = normalizeForSearch(query);
  if (!q) return 0;
  const tokens = q.split(' ').filter(Boolean);
  if (tokens.length === 0) return 0;

  const fields = collectScoredFields(item, section);
  let total = 0;
  for (const token of tokens) {
    let bestWeight = 0;
    for (const field of fields) {
      if (field.weight > bestWeight && field.text.includes(token)) {
        bestWeight = field.weight;
      }
    }
    total += bestWeight;
  }
  return total;
};
